import React, { useEffect, useMemo, useState } from 'react';
import {
  Appointment,
  MedicalCertificate,
  QuestionnaireSubmission,
  QuestionnaireTemplate,
  Service,
  SystemConfig
} from '@/types';
import { useAppointmentMutations } from '@features/admin/data';
import {
  ClipboardCheck,
  FileText,
  Loader2,
  MailCheck,
  Printer,
  Trash2,
  XCircle
} from 'lucide-react';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { AppointmentDocumentsPanel } from '@shared/components/common/AppointmentDocumentsPanel';
import { StatusBadge } from '@shared/components/common/StatusBadge';
import { formatUkDate, formatUkDateTime } from '@/lib/dates';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { buildMapById } from '@/lib/collections';
import {
  buildReviewAnswerEntries,
  formatReviewFieldValue,
  toTitleCase
} from '@/lib/questionnaireReview';
import {
  buildCertificateFileName,
  buildCertificateHtml,
  openBlankPrintWindow,
  writeCertificatePrintWindow,
  resolveCertificateClientName
} from '@/lib/certificateRenderer';
import { api } from '@/services/api';
import { getOutcomeChipClass } from './CustomerHistoryPanel';

interface CustomerMedicalReviewProps {
  appointment: Appointment | null;
  services: Service[];
  questionnaires: QuestionnaireTemplate[];
  systemConfig?: SystemConfig;
  canRevokeCertificate?: boolean;
  canOverrideLockedPaperwork?: boolean;
  isHighlighted?: boolean;
}

type ResolvedCertificate = Omit<Partial<MedicalCertificate>, 'outcome'> & {
  id: string;
  outcome?: string | null;
};

export const CustomerMedicalReview: React.FC<CustomerMedicalReviewProps> = ({
  appointment,
  services,
  questionnaires,
  systemConfig,
  canRevokeCertificate = false,
  canOverrideLockedPaperwork = false,
  isHighlighted = false
}) => {
  const appointmentMutations = useAppointmentMutations();
  const [certificate, setCertificate] = useState<MedicalCertificate | null>(null);
  const [submissions, setSubmissions] = useState<QuestionnaireSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dataError, setDataError] = useState('');
  const [removingScanId, setRemovingScanId] = useState<string | null>(null);

  useEffect(() => {
    if (!appointment) {
      setCertificate(null);
      setSubmissions([]);
      setDataError('');
      return;
    }
    let isActive = true;
    setIsLoading(true);
    setDataError('');

    Promise.allSettled([
      api.listStaffCertificates({ appointmentId: appointment.id, page: 1, pageSize: 1 }),
      api.listStaffSubmissions({ appointmentIds: appointment.id, page: 1, pageSize: 20 })
    ])
      .then(([certResult, submissionResult]) => {
        if (!isActive) return;
        const errors: string[] = [];

        if (certResult.status === 'fulfilled') {
          setCertificate(certResult.value.items?.[0] || null);
        } else {
          setCertificate(null);
          if (appointment.certificateSummary?.id) {
            errors.push('Certificate details are currently unavailable.');
          }
        }

        if (submissionResult.status === 'fulfilled') {
          setSubmissions(submissionResult.value.items || []);
        } else {
          setSubmissions([]);
          if (appointment.questionnaireStatus !== 'not_required') {
            errors.push('Questionnaire submissions could not be loaded.');
          }
        }

        setDataError(errors.join(' '));
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [appointment?.id, appointment?.certificateSummary?.id, appointment?.questionnaireStatus]);

  const scanRemovalLocked = Boolean(certificate) && (certificate?.status ?? 'active') === 'active';

  const servicesById = useMemo(() => buildMapById(services), [services]);
  const questionnaireById = useMemo(
    () => new Map((questionnaires || []).map((q) => [q.id, q])),
    [questionnaires]
  );

  const serviceFallbackTemplateId = servicesById.get(appointment?.serviceId || '')
    ?.questionnaireConfig?.templateId;

  const resolveTemplate = (submission: QuestionnaireSubmission): QuestionnaireTemplate | null => {
    if (submission.templateId && questionnaireById.has(submission.templateId)) {
      return questionnaireById.get(submission.templateId) || null;
    }
    if (serviceFallbackTemplateId && questionnaireById.has(serviceFallbackTemplateId)) {
      return questionnaireById.get(serviceFallbackTemplateId) || null;
    }
    return null;
  };

  const resolvedCertificate: ResolvedCertificate | null =
    certificate ||
    (appointment?.certificateSummary
      ? {
          id: appointment.certificateSummary.id,
          appointmentId: appointment.id,
          issuedAt: appointment.certificateSummary.issuedAt || '',
          validUntil: appointment.certificateSummary.validUntil || '',
          technicianName: '',
          outcome: appointment.certificateSummary.outcome || null
        }
      : null);

  const handleResendCertEmail = async (appId: string) => {
    const confirmed = await showConfirmModal(
      "Re-send the 'Certificate Issued' email to this patient?",
      {
        title: 'Resend Certificate Email',
        confirmText: 'Resend',
        cancelText: 'Cancel',
        isDestructive: false
      }
    );
    if (!confirmed) return;
    try {
      await appointmentMutations.sendReminder(appId, 'email', 'certificate_issued');
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to resend certificate email.'));
    }
  };

  const handleRemovePaperScan = async (submission: QuestionnaireSubmission) => {
    const isOverride = scanRemovalLocked;
    const message = isOverride
      ? 'A certificate has already been issued for this appointment. Removing this paper questionnaire will NOT revoke the certificate — use it only to correct paperwork filed against the wrong candidate. The scan will be deleted and the questionnaire will show as pending again. Continue?'
      : 'Remove this uploaded paper questionnaire? The scan will be deleted and the questionnaire will show as pending again.';
    const confirmed = await showConfirmModal(message, {
      title: isOverride ? 'Override — Remove Locked Paperwork' : 'Remove Paper Questionnaire',
      confirmText: 'Remove',
      isDestructive: true
    });
    if (!confirmed) return;
    setRemovingScanId(submission.id);
    try {
      await api.removePaperQuestionnaire(submission.id, { override: isOverride });
      setSubmissions((prev) => prev.filter((s) => s.id !== submission.id));
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to remove paper questionnaire.'));
    } finally {
      setRemovingScanId(null);
    }
  };

  const handleViewCertificate = async (app: Appointment) => {
    if (!systemConfig) return;
    const printWindow = openBlankPrintWindow();
    if (!printWindow) {
      showAlertModal(
        'Your browser blocked the certificate window. Please allow pop-ups for this site and try again.'
      );
      return;
    }
    let cert: MedicalCertificate | null = null;
    try {
      const result = await api.listStaffCertificates({
        appointmentId: app.id,
        page: 1,
        pageSize: 1
      });
      cert = result.items?.[0] || null;
    } catch (err: unknown) {
      printWindow.close();
      showAlertModal(normalizeErrorMessage(err, 'Failed to load certificate.'));
      return;
    }
    if (!cert) {
      printWindow.close();
      showAlertModal('Certificate not found for this appointment.');
      return;
    }
    const html = buildCertificateHtml({
      template: systemConfig.templates.certificateTemplate.body,
      systemConfig,
      appointment: app,
      certificate: cert,
      clientName: resolveCertificateClientName(app)
    });
    writeCertificatePrintWindow(
      html,
      buildCertificateFileName({ appointment: app, certificate: cert }),
      printWindow
    );
  };

  const handleRevokeCertificate = async (certificateId: string) => {
    const reason = window.prompt(
      'Revoke this certificate? Optional reason (max 500 chars, kept internal):',
      ''
    );
    if (reason === null) return;
    const confirmed = await showConfirmModal(
      `This will mark certificate ${certificateId} as revoked. The public verification page will show "Certificate Revoked" instead of the certificate details. Continue?`,
      {
        title: 'Revoke Certificate',
        confirmText: 'Revoke',
        cancelText: 'Cancel',
        isDestructive: true
      }
    );
    if (!confirmed) return;
    try {
      const result = await api.revokeCertificate(certificateId, reason);
      setCertificate(result.certificate);
      showAlertModal('Certificate revoked.');
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to revoke certificate.'));
    }
  };

  return (
    <div>
      <h4 className="font-black text-xs text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
        <ClipboardCheck className="w-4 h-4" /> Medical Data Review
      </h4>
      <div
        className={`bg-white border rounded-xl p-4 shadow-sm min-h-[460px] transition-all duration-500 ${
          isHighlighted
            ? 'border-teal-400 ring-4 ring-teal-200 shadow-lg'
            : 'border-slate-200 ring-0 ring-transparent'
        }`}
      >
        {!appointment ? (
          <p className="text-sm text-slate-500 italic">
            Select a booking from clinical history to review its medical data.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-bold text-slate-900 text-sm">
                  {formatUkDate(appointment.date, '—')} at {appointment.time}
                </p>
                <p className="text-xs text-slate-500">
                  Ref: {appointment.id}
                  {appointment.serviceName ? ` | ${appointment.serviceName}` : ''}
                </p>
              </div>
              <StatusBadge
                status={appointment.status}
                className="text-[9px] tracking-widest px-2 py-0.5"
              />
            </div>

            <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">
                Certificate Outcome
              </span>
              <span
                className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-lg border ${getOutcomeChipClass(resolvedCertificate?.outcome || 'pending')}`}
              >
                {toTitleCase(String(resolvedCertificate?.outcome || 'pending'))}
              </span>
            </div>

            {isLoading && (
              <div className="text-xs text-slate-500 flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Loading linked medical data...
              </div>
            )}
            {dataError && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                {dataError}
              </div>
            )}

            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">
                Appointment Snapshot
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-700">
                <div>
                  <span className="text-slate-400 block">Location</span>
                  {appointment.venueName || '-'}
                </div>
                <div>
                  <span className="text-slate-400 block">Company</span>
                  {appointment.companyName || 'Individual'}
                </div>
                <div>
                  <span className="text-slate-400 block">Questionnaire</span>
                  {toTitleCase(appointment.questionnaireStatus || 'not_required')}
                </div>
                <div>
                  <span className="text-slate-400 block">Booking Status</span>
                  {toTitleCase(appointment.status)}
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">
                Clinical Notes
              </p>
              <div className="space-y-2">
                {[
                  {
                    label: 'Technician Notes',
                    value: appointment.technicianNotes,
                    fallback: 'No technician notes recorded.'
                  },
                  {
                    label: 'Staff Private Note',
                    value: appointment.staffPrivateNote,
                    fallback: 'No private note.'
                  },
                  {
                    label: 'Technician Private Reply',
                    value: appointment.technicianPrivateReply,
                    fallback: 'No private reply.'
                  }
                ].map(({ label, value, fallback }) => (
                  <div key={label} className="bg-slate-50 border border-slate-100 rounded-lg p-2.5">
                    <p className="text-[10px] font-black uppercase text-slate-400">{label}</p>
                    <p className="text-xs text-slate-700 whitespace-pre-wrap">
                      {value?.trim() || fallback}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">
                Certificate Details
              </p>
              {resolvedCertificate ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-700">
                    <div>
                      <span className="text-slate-400 block">Issued</span>
                      {resolvedCertificate.issuedAt
                        ? formatUkDate(resolvedCertificate.issuedAt, '-')
                        : '-'}
                    </div>
                    <div>
                      <span className="text-slate-400 block">Valid Until</span>
                      {resolvedCertificate.validUntil
                        ? formatUkDate(resolvedCertificate.validUntil, '-')
                        : '-'}
                    </div>
                    <div>
                      <span className="text-slate-400 block">Drug Test</span>
                      {resolvedCertificate.drugTestOutcome
                        ? toTitleCase(resolvedCertificate.drugTestOutcome)
                        : 'Not recorded'}
                    </div>
                    <div>
                      <span className="text-slate-400 block">Certificate ID</span>
                      {resolvedCertificate.id}
                    </div>
                  </div>
                  {resolvedCertificate.restrictions && (
                    <p className="text-xs text-slate-700 mt-2">
                      <span className="text-slate-400">Restrictions: </span>
                      {resolvedCertificate.restrictions}
                    </p>
                  )}
                  {resolvedCertificate.advice && (
                    <p className="text-xs text-slate-700 mt-1">
                      <span className="text-slate-400">Advice: </span>
                      {resolvedCertificate.advice}
                    </p>
                  )}
                  {resolvedCertificate.status === 'revoked' && (
                    <p className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-widest text-red-700 bg-red-50 border border-red-200">
                      Revoked
                      {resolvedCertificate.revokedAt
                        ? ` · ${formatUkDate(resolvedCertificate.revokedAt, '-')}`
                        : ''}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button
                      onClick={() => handleViewCertificate(appointment)}
                      disabled={!systemConfig}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-teal-700 border border-teal-200 bg-teal-50 hover:bg-teal-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Printer className="w-3.5 h-3.5" /> View Certificate
                    </button>
                    <button
                      onClick={() => handleResendCertEmail(appointment.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-all"
                    >
                      <MailCheck className="w-3.5 h-3.5" /> Resend Certificate Email
                    </button>
                    {canRevokeCertificate && resolvedCertificate.status !== 'revoked' && (
                      <button
                        onClick={() => handleRevokeCertificate(resolvedCertificate.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-red-700 border border-red-200 bg-red-50 hover:bg-red-100 transition-all"
                      >
                        <XCircle className="w-3.5 h-3.5" /> Revoke Certificate
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-xs text-slate-500 italic">
                  No certificate linked to this appointment yet.
                </p>
              )}
            </div>

            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">
                Questionnaire Forms
              </p>
              {submissions.length > 0 ? (
                <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                  {submissions.map((submission) => {
                    if (submission.scanUrl) {
                      return (
                        <div
                          key={submission.id}
                          className="bg-amber-50/60 border border-amber-100 rounded-lg p-2.5"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-[10px] font-black uppercase text-amber-700 tracking-widest">
                              Paper Questionnaire
                            </p>
                            <span className="text-[10px] text-slate-400">
                              Uploaded: {formatUkDateTime(submission.submittedAt, '—')}
                            </span>
                          </div>
                          <a
                            href={submission.scanUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-teal-700 border border-teal-200 bg-teal-50 hover:bg-teal-100 transition-all"
                          >
                            <FileText className="w-3.5 h-3.5" /> View Scanned Form
                          </a>
                          {(!scanRemovalLocked || canOverrideLockedPaperwork) && (
                            <button
                              type="button"
                              disabled={removingScanId === submission.id}
                              onClick={() => handleRemovePaperScan(submission)}
                              className="mt-2 ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              {removingScanId === submission.id
                                ? 'Removing…'
                                : scanRemovalLocked
                                  ? 'Override & Remove'
                                  : 'Remove'}
                            </button>
                          )}
                          {scanRemovalLocked && (
                            <p className="mt-2 text-[11px] text-amber-700/90 leading-snug">
                              <span className="font-bold">Locked:</span> a certificate has been
                              issued for this appointment.
                              {canOverrideLockedPaperwork
                                ? ' Use “Override & Remove” only to correct paperwork filed against the wrong candidate — it does not revoke the certificate.'
                                : ' Removing it requires an administrator (Settings permission).'}
                            </p>
                          )}
                          {submission.technicianComments?.['paper']?.trim() && (
                            <p className="mt-2 text-xs text-slate-600">
                              <span className="font-bold text-slate-400">Technician note: </span>
                              {submission.technicianComments['paper']}
                            </p>
                          )}
                        </div>
                      );
                    }

                    const template = resolveTemplate(submission);
                    const entries = buildReviewAnswerEntries(submission.answers, template);
                    return (
                      <div
                        key={submission.id}
                        className="border border-slate-100 rounded-lg overflow-hidden"
                      >
                        <div className="bg-slate-50 px-2.5 py-1.5 border-b border-slate-100 flex items-center justify-between gap-2">
                          <p className="text-[10px] font-black uppercase text-slate-500 tracking-widest">
                            {template?.name || 'Submitted Answers'}
                          </p>
                          <span className="text-[10px] text-slate-400">
                            Completed: {formatUkDateTime(submission.submittedAt, '—')}
                          </span>
                        </div>
                        {entries.length > 0 ? (
                          <div className="space-y-2 p-2.5">
                            {entries.map((entry) => (
                              <div
                                key={entry.key}
                                className="bg-slate-50 border border-slate-100 rounded-lg p-2.5"
                              >
                                <p className="text-[10px] font-black uppercase text-slate-400">
                                  {entry.label}
                                </p>
                                <p className="text-xs text-slate-700 whitespace-pre-wrap break-words">
                                  {formatReviewFieldValue(entry.value)}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-500 italic p-2.5">
                            No answers recorded on this submission.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : appointment.questionnaireStatus === 'pending' ? (
                <p className="text-xs text-slate-500 italic">
                  Questionnaire is pending and has not been submitted.
                </p>
              ) : appointment.questionnaireStatus === 'not_required' ? (
                <p className="text-xs text-slate-500 italic">
                  Questionnaire was not required for this appointment.
                </p>
              ) : (
                <p className="text-xs text-slate-500 italic">
                  No questionnaire answers available for review.
                </p>
              )}
            </div>

            <AppointmentDocumentsPanel appointmentId={appointment.id} />
          </div>
        )}
      </div>
    </div>
  );
};
